import React, { useState, useMemo } from 'react';
import { auth } from '../firebase';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Coins, AlertTriangle, Plus, Minus } from 'lucide-react';

const fmtCents = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const AdjustCreditModal = ({
  isOpen,
  onClose,
  companyId,
  leadId,
  currentBalanceCents = 0,
  onSuccess,
}) => {
  // UI sugar: radio toggle for direction. The server contract is signed `deltaCents`.
  const [direction, setDirection] = useState('add');     // 'add' | 'subtract'
  const [amount,    setAmount]    = useState('');        // dollars, as-typed
  const [reason,    setReason]    = useState('');
  const [notes,     setNotes]     = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Parse amount in dollars → cents (integer, rounded).
  const amountCents = useMemo(() => {
    const dollars = parseFloat(amount);
    if (!Number.isFinite(dollars) || dollars <= 0) return 0;
    return Math.round(dollars * 100);
  }, [amount]);

  const signedDeltaCents = direction === 'subtract' ? -amountCents : amountCents;
  const previewBalance   = currentBalanceCents + signedDeltaCents;
  const wouldGoNegative  = previewBalance < 0;
  const canSubmit =
    !submitting && amountCents > 0 && reason.trim().length > 0 && !wouldGoNegative;

  const handleSubmit = async () => {
    if (!canSubmit || !leadId || !companyId) return;
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${backendUrl}/api/leads/${leadId}/credits/entry`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            companyId,
            deltaCents: signedDeltaCents,
            reason: reason.trim(),
            notes: notes.trim() || undefined,
          }),
        }
      );
      let data = {};
      try { data = await res.json(); } catch (parseErr) { console.warn(parseErr); }

      if (res.ok && data.success) {
        toast.success(
          `${direction === 'add' ? 'Added' : 'Subtracted'} ${fmtCents(amountCents)} · New balance ${fmtCents(data.balanceAfterCents)}`
        );
        onSuccess?.(data);
        // Reset for next open
        setAmount('');
        setReason('');
        setNotes('');
        setDirection('add');
        onClose?.();
      } else if (res.status === 403) {
        toast.error(data.detail || 'Credit ledger requires owner or admin role.');
      } else {
        toast.error(data.detail || 'Failed to record credit entry.');
      }
    } catch (err) {
      console.error('[AdjustCreditModal] Network error:', err);
      toast.error('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const isAdd = direction === 'add';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="adjust-credit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-indigo-600" />
            Adjust Account Credit
          </DialogTitle>
          <DialogDescription>
            Posts an immutable credit-ledger entry. Positive values add credit,
            negative values subtract. The server rejects adjustments that would
            create a negative balance.
          </DialogDescription>
        </DialogHeader>

        {/* Current balance summary */}
        <div
          className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 flex items-center justify-between"
          data-testid="adjust-credit-current-balance"
        >
          <span className="text-xs font-medium text-indigo-700 uppercase tracking-wide">
            Current balance
          </span>
          <span className="text-base font-semibold text-indigo-900 tabular-nums">
            {fmtCents(currentBalanceCents)}
          </span>
        </div>

        {/* Direction toggle */}
        <div className="space-y-1.5">
          <Label className="text-xs">Direction</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setDirection('add')}
              data-testid="adjust-credit-direction-add"
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                isAdd
                  ? 'bg-green-50 border-green-300 text-green-800'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Plus className="h-3.5 w-3.5" />
              Add credit
            </button>
            <button
              type="button"
              onClick={() => setDirection('subtract')}
              data-testid="adjust-credit-direction-subtract"
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                !isAdd
                  ? 'bg-orange-50 border-orange-300 text-orange-800'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Minus className="h-3.5 w-3.5" />
              Subtract credit
            </button>
          </div>
        </div>

        {/* Amount */}
        <div className="space-y-1.5">
          <Label htmlFor="adjust-credit-amount" className="text-xs">
            Amount <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium pointer-events-none">
              $
            </span>
            <Input
              id="adjust-credit-amount"
              data-testid="adjust-credit-amount"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="pl-7"
              autoFocus
            />
          </div>
        </div>

        {/* Reason */}
        <div className="space-y-1.5">
          <Label htmlFor="adjust-credit-reason" className="text-xs">
            Reason / reference <span className="text-red-500">*</span>
          </Label>
          <Input
            id="adjust-credit-reason"
            data-testid="adjust-credit-reason"
            placeholder="e.g. Service recovery, operator correction, goodwill credit"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
          />
        </div>

        {/* Optional notes */}
        <div className="space-y-1.5">
          <Label htmlFor="adjust-credit-notes" className="text-xs">
            Notes <span className="text-gray-400 font-normal normal-case">(optional)</span>
          </Label>
          <Input
            id="adjust-credit-notes"
            data-testid="adjust-credit-notes"
            placeholder="Internal narrative — visible on the ledger only"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
          />
        </div>

        {/* Preview */}
        <div
          className={`flex items-center justify-between px-3 py-2 rounded-md border text-sm ${
            wouldGoNegative
              ? 'bg-red-50 border-red-200 text-red-700'
              : amountCents > 0
                ? 'bg-gray-50 border-gray-200 text-gray-700'
                : 'bg-gray-50 border-gray-100 text-gray-400'
          }`}
          data-testid="adjust-credit-preview"
        >
          <span className="text-xs">Preview</span>
          {amountCents > 0 ? (
            <span className="tabular-nums">
              {fmtCents(currentBalanceCents)}
              {' → '}
              <strong>{fmtCents(previewBalance)}</strong>
              <span className={`ml-1 ${isAdd ? 'text-green-700' : 'text-orange-700'}`}>
                ({isAdd ? '+' : '−'}{fmtCents(amountCents)})
              </span>
            </span>
          ) : (
            <span className="text-xs">Enter an amount</span>
          )}
        </div>

        {wouldGoNegative && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs"
            data-testid="adjust-credit-negative-error"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              This would create a negative balance ({fmtCents(previewBalance)}). The
              server will reject it — reduce the amount or switch direction.
            </span>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onClose?.()}
            disabled={submitting}
            data-testid="adjust-credit-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={isAdd ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}
            data-testid="adjust-credit-submit-btn"
          >
            {submitting ? 'Posting…' : `Apply ${isAdd ? '+' : '−'}${fmtCents(amountCents)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustCreditModal;
